import { useEffect, useState } from "react";
import api, { serializeSearchParams } from "../../actprep-ui/utils/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import { Stack } from "@mui/system";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  FormGroup,
  List,
  ListItem,
  ListItemText,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Paper,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MobileDatePicker } from "@mui/x-date-pickers-pro";
import { useConfirm } from "material-ui-confirm";
import RRuleEditor, {
  defaultRRuleString,
} from "../../actprep-ui/components/RRuleEditor/RRuleEditor";

export default function ToDoEventTriggerForm({ onSubmit, eventId }) {
  const confirm = useConfirm();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(null);
  const [isRepeating, setIsRepeating] = useState(false);
  const [recurrence, setRecurrence] = useState(defaultRRuleString());
  const [repeatStartDate, setRepeatStartDate] = useState(null);
  const [repeatEndDate, setRepeatEndDate] = useState(null);
  const [error, setError] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [audienceOptions, setAudienceOptions] = useState({ roles: [], teams: [] });
  const [audienceSelections, setAudienceSelections] = useState({
    roles: {},
    teams: {},
  });

  useEffect(() => {
    let isMounted = true;

    const loadAudienceOptions = async () => {
      setAudienceLoading(true);
      setError(null);
      try {
        const response = await api.get(apiUrls.admin.toDoEventTriggers(), {
          params: serializeSearchParams({
            search_params: {
              event_id: eventId,
            },
            include_audience_options: true,
          }),
        });

        if (!isMounted) return;
        if (!response.data?.success) {
          setError(response.data?.reason || "Unable to load audience options.");
          return;
        }

        const options = response.data?.data || { roles: [], teams: [] };
        const nextSelections = { roles: {}, teams: {} };
        for (const role of options.roles || []) {
          nextSelections.roles[role.uuid] = (role.users || []).map((u) => u.uuid);
        }
        for (const team of options.teams || []) {
          nextSelections.teams[team.uuid] = (team.users || []).map((u) => u.uuid);
        }
        setAudienceOptions(options);
        setAudienceSelections(nextSelections);
      } catch {
        if (!isMounted) return;
        setError("Unable to load audience options.");
      } finally {
        if (isMounted) {
          setAudienceLoading(false);
        }
      }
    };

    loadAudienceOptions();
    return () => {
      isMounted = false;
    };
  }, [eventId]);

  const toggleAudienceUser = (audienceType, groupId, userId) => {
    setAudienceSelections((prev) => {
      const current = prev[audienceType]?.[groupId] || [];
      const isSelected = current.includes(userId);

      const nextIds = isSelected
        ? current.filter((id) => id !== userId)
        : [...current, userId];

      return {
        ...prev,
        [audienceType]: {
          ...prev[audienceType],
          [groupId]: nextIds,
        },
      };
    });
  };

  const handleDeselectAll = () => {
    const nextSelections = { roles: {}, teams: {} };
    for (const audienceType of ["roles", "teams"]) {
      const groups = audienceOptions[audienceType] || [];
      for (const group of groups) {
        nextSelections[audienceType][group.uuid] = [];
      }
    }
    setAudienceSelections(nextSelections);
  };

  const hasInvalidAudienceSelection = ["roles", "teams"].some((audienceType) =>
    (audienceOptions[audienceType] || []).some((group) => {
      if (!(group.users || []).length) return false;
      const selectedIds = audienceSelections[audienceType]?.[group.uuid] || [];
      return selectedIds.length < 1;
    })
  );

  const buildAudienceInclusions = () => {
    const payload = {
      roles: {},
      teams: {},
    };

    for (const audienceType of ["roles", "teams"]) {
      const groups = audienceOptions[audienceType] || [];
      for (const group of groups) {
        const allUserIds = (group.users || []).map((user) => user.uuid);
        if (!allUserIds.length) continue;

        const selectedIds =
          audienceSelections[audienceType]?.[group.uuid] || allUserIds;
        const selectedSet = new Set(selectedIds);
        const isFullSelection =
          selectedIds.length === allUserIds.length &&
          allUserIds.every((id) => selectedSet.has(id));

        if (!isFullSelection) {
          payload[audienceType][group.uuid] = selectedIds;
        }
      }
    }

    return payload;
  };

  const buildPayload = () => {
    const payload = {
      title,
      event_id: eventId,
      repeating: isRepeating,
      audience_inclusions: buildAudienceInclusions(),
    };

    if (isRepeating) {
      payload.date = null;
      payload.recurrence = recurrence;
      payload.repeat_start_date = repeatStartDate?.format("YYYY-MM-DD") ?? null;
      payload.repeat_end_date = repeatEndDate?.format("YYYY-MM-DD") ?? null;
    } else {
      payload.recurrence = null;
      payload.repeat_start_date = null;
      payload.repeat_end_date = null;
      payload.date = date?.format("YYYY-MM-DD") ?? null;
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    for (const audienceType of ["roles", "teams"]) {
      const groups = audienceOptions[audienceType] || [];
      for (const group of groups) {
        if (!(group.users || []).length) continue;
        const selectedIds = audienceSelections[audienceType]?.[group.uuid] || [];
        if (!selectedIds.length) {
          setError(`Select at least one user for ${group.title}.`);
          return;
        }
      }
    }

    const payload = buildPayload();

    try {
      const previewResponse = await api.post(apiUrls.admin.toDoEventTriggers(), {
        ...payload,
        preview: true,
      });
      if (!previewResponse.data?.success) {
        setError(previewResponse.data?.reason ?? "Unable to preview trigger.");
        return;
      }

      const immediateItems = previewResponse.data?.data?.immediate_items || [];
      if (immediateItems.length) {
        try {
          await confirm({
            title: "To-Do Items Will Be Added Today",
            content: (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Creating this trigger will immediately add the following to-do
                  items for today:
                </Typography>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <List dense disablePadding>
                    {immediateItems.map((item) => (
                      <ListItem key={item.uuid} disablePadding sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={item.title}
                          secondary={
                            item.event_occurrence_offset
                              ? `Offset: ${item.event_occurrence_offset} day${
                                  item.event_occurrence_offset === 1 ? "" : "s"
                                }`
                              : null
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            ),
            confirmationText: "Create Trigger",
            cancellationText: "Cancel",
          });
        } catch {
          return;
        }
      }

      const response = await api.post(apiUrls.admin.toDoEventTriggers(), payload);
      if (response.data?.success) {
        showSuccessSnackbar("Trigger created!");
        onSubmit(response.data.data);
      } else {
        setError(response.data?.reason ?? "Unable to create trigger.");
      }
    } catch {
      setError("Unable to create trigger.");
    }
  };

  return (
    <Stack spacing={2} component={"form"} onSubmit={handleSubmit}>
      <TextField
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        label="Title"
        required
        variant="standard"
      />

      <FormControl component="fieldset">
        <FormLabel component="legend">Trigger Type</FormLabel>
        <RadioGroup
          value={isRepeating ? "repeating" : "single"}
          onChange={(event) =>
            setIsRepeating(event.target.value === "repeating")
          }
          row
        >
          <FormControlLabel
            value="single"
            control={<Radio />}
            label="Single Occurrence"
          />
          <FormControlLabel
            value="repeating"
            control={<Radio />}
            label="Repeating"
          />
        </RadioGroup>
      </FormControl>

      {!isRepeating ? (
        <MobileDatePicker
          value={date}
          onChange={(newVal) => setDate(newVal)}
          label="Date"
          closeOnSelect
          slotProps={{
            textField: {
              variant: "standard",
            },
          }}
        />
      ) : (
        <Stack spacing={2}>
          <Typography variant="subtitle2">Recurrence Pattern</Typography>
          <RRuleEditor
            value={recurrence}
            onChange={(newRecurrence) => setRecurrence(newRecurrence)}
          />

          <Typography variant="subtitle2">Date Range (Optional)</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <MobileDatePicker
              value={repeatStartDate}
              onChange={(newVal) => setRepeatStartDate(newVal)}
              label="Start Date"
              closeOnSelect
              slotProps={{
                textField: {
                  variant: "standard",
                },
              }}
            />
            <MobileDatePicker
              value={repeatEndDate}
              onChange={(newVal) => setRepeatEndDate(newVal)}
              label="End Date"
              closeOnSelect
              slotProps={{
                textField: {
                  variant: "standard",
                },
              }}
            />
          </Stack>
        </Stack>
      )}

      <Stack spacing={1}>
        <Typography variant="subtitle2">Role & Team Audience Overrides</Typography>
        <Typography variant="caption" color="text.secondary">
          You can deselect affected users for each role/team.
        </Typography>
        <Stack direction="row" justifyContent="flex-end">
          <Chip
            size="small"
            label="Deselect All"
            onClick={handleDeselectAll}
            disabled={
              audienceLoading ||
              (!audienceOptions.roles?.length && !audienceOptions.teams?.length)
            }
            clickable
          />
        </Stack>

        {audienceLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading audience groups...
            </Typography>
          </Stack>
        ) : (
          <>
            {["roles", "teams"].map((audienceType) =>
              (audienceOptions[audienceType] || []).map((group) => {
                const selectedIds =
                  audienceSelections[audienceType]?.[group.uuid] || [];
                const totalUsers = (group.users || []).length;
                return (
                  <Paper key={`${audienceType}-${group.uuid}`} variant="outlined">
                    <Accordion disableGutters elevation={0}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ width: "100%", pr: 1 }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {group.title}{" "}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              ({audienceType === "roles" ? "Role" : "Team"})
                            </Typography>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Affected users: {selectedIds.length}/{totalUsers}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        {totalUsers ? (
                          <FormGroup sx={{ mt: 0.5 }}>
                            {(group.users || []).map((user) => {
                              const isSelected = selectedIds.includes(user.uuid);
                              return (
                                <FormControlLabel
                                  key={user.uuid}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={isSelected}
                                      onChange={() =>
                                        toggleAudienceUser(
                                          audienceType,
                                          group.uuid,
                                          user.uuid
                                        )
                                      }
                                    />
                                  }
                                  label={user.label}
                                />
                              );
                            })}
                          </FormGroup>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No users currently match this group.
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Paper>
                );
              })
            )}
            {!audienceOptions.roles?.length && !audienceOptions.teams?.length && (
              <Typography variant="body2" color="text.secondary">
                No role/team audiences are attached to this event&apos;s to-do
                items.
              </Typography>
            )}
          </>
        )}
      </Stack>

      {error && (
        <Alert sx={{ mt: 1 }} severity="error">
          {error}
        </Alert>
      )}
      {!audienceLoading && hasInvalidAudienceSelection && (
        <Alert sx={{ mt: 1 }} severity="warning">
          Select at least one user for each role/team before creating the trigger.
        </Alert>
      )}
      <Button fullWidth type="submit" disabled={hasInvalidAudienceSelection}>
        Create Trigger
      </Button>
    </Stack>
  );
}
